import { useToast } from '@workramp/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { I18n } from '~/common/explicit-exports';

import { getDownloadURLQueryKey, useGetDownloadURL } from '../api';
import { downloadFile } from '../utils';

type FileDownloaderParams = {
  assetId: string;
  name: string;
  onDownloadComplete?: () => void;
};

export const useFileDownloader = ({ assetId, name, onDownloadComplete }: FileDownloaderParams) => {
  // IMPORTANT: We need to use a state variable to control getting the download URL, so it doesn't get called on every render of the consuming component.
  // This essentially ensures we only call the download URL API once per click of the download button.
  const [shouldGetDownloadURL, setShouldGetDownloadURL] = useState<boolean>(false);

  const fileDownloadURL = useGetDownloadURL(assetId, { enabled: shouldGetDownloadURL });
  const queryClient = useQueryClient();
  const { open: openToast } = useToast();
  const TRANSLATED_DOWNLOADING = I18n.t('js.admin.cms.downloading');

  useEffect(() => {
    const executeDownload = async () => {
      const url = fileDownloadURL?.data?.fileUrl;
      // Only download the file if we have a URL and user clicked download button (shouldGetDownloadURL is true)
      if (url && shouldGetDownloadURL) {
        await downloadFile(url, name);

        onDownloadComplete?.();
        setShouldGetDownloadURL(false);
        // Remove the query from the cache to force a refetch when the user clicks the download button again
        // This is necessary because the URL is an S3 pre-signed URL that expires after a certain amount of time
        queryClient.removeQueries(getDownloadURLQueryKey(assetId));
      }
    };

    executeDownload();
  }, [fileDownloadURL?.data?.fileUrl, name, assetId, queryClient, onDownloadComplete, shouldGetDownloadURL]);

  const download = () => {
    setShouldGetDownloadURL(true);
    openToast({ children: TRANSLATED_DOWNLOADING });
  };

  return { downloadFile: download, isDownloading: fileDownloadURL?.isInitialLoading || shouldGetDownloadURL };
};
